import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { DateTime } from "luxon"
import DirectoryTable from "../lib/directory_table"
import { bindRowPreviewPrefetch } from "../lib/resource_preview_prefetch"
import TableScrollMemory from "../lib/table_scroll_memory"
import { ageColumn, isGroupRow, boardListMark, createAttributeFilterFormatter, stateBadge, columnPersistenceOptions, columnVisibilityHeaderMenu, emptyStatePlaceholder, emptyStateFor, rowSelectionRowHeader } from "./tabulator_helpers"

export default class extends Controller {
  boundDownloadCsv = this.downloadCsv.bind(this)
  boundStopInteractiveClick = this.stopInteractiveClick.bind(this)
  static targets = [
    'tasks',
    'table',
    'actions',
    'actionAllButton',
    'aiSelectButton',
    'queryTitleBanner',
    'queryTitleContent'
  ]

  static values = {
    url: String,
    tabulatorTable: Object,
    agentKey: String,
    filtersOpen: Boolean,
    selectedRows: Array,
    lists: Array, // [{id, name, color, color_classes}]
    taskKinds: Boolean, // the account defines at least one kind
    groupSimilar: Boolean, // the person's "Group similar" view (User#group_similar_tasks)
  }

  // A parent row of the "Group similar" view: look-alike tasks under one row
  // (PaperworkTasksController#grouped_index_json). It is not a task: it has no
  // reference, no checkbox and no preview.
  isGroupRow(data) {
    return isGroupRow(data)
  }

  // The parent's title: the count, who the tasks are about (the contact, or
  // the sender when there is no contact yet; the shared kind and reason sit in
  // the kind column), and one quiet button that opens what to do with the group.
  groupTitleFormatter(cell) {
    const data = cell.getRow().getData()
    const wrapper = document.createElement("span")
    wrapper.className = "wf-subject task-group-title"
    const count = document.createElement("span")
    count.className = "task-group-title__count"
    count.textContent = `${data.group.count} tasks`
    const issue = document.createElement("span")
    issue.className = "task-group-title__issue"
    issue.textContent = data.group.contact || data.group.party || data.title || "Look alike"
    const act = document.createElement("button")
    act.type = "button"
    act.className = "btn btn-xs task-group-title__act"
    act.textContent = { map: "Map to contact", each: "Work together" }[data.group.lead] || "Handle all"
    act.addEventListener("click", (event) => {
      event.stopPropagation()
      this.openGroup(data.group)
    })
    wrapper.append(count, issue, act)
    return wrapper
  }

  // Who the group is about: its contact, or with no contact yet the sender
  // its workflows share. Either is left out for a viewer who may not read
  // contacts.
  groupPartyFormatter(cell) {
    const group = cell.getRow().getData().group
    const party = document.createElement("span")
    party.className = "text-sm"
    if (group.contact) {
      party.textContent = group.contact
    } else if (group.party) {
      party.className = "text-sm text-ink-soft"
      party.textContent = group.party
      party.title = "No contact yet: grouped by who the tasks are about (the name or address they ask about, or the sender of each workflow's first email)"
    }
    return party
  }

  openGroup(group) {
    if (!group?.anchor) return
    const params = new URLSearchParams()
    params.set("from", "list")
    params.set("references", (group.references || []).join(","))
    params.set("total", group.count)
    fetch(`/tasks/${encodeURIComponent(group.anchor)}/similar_group?${params.toString()}`, {
      headers: { 'Accept': 'text/vnd.turbo-stream.html' }
    })
      .then(response => response.text())
      .then(html => {
        if (html.trim().startsWith("<turbo-stream")) Turbo.renderStreamMessage(html)
      })
      .catch(error => console.error('Error opening the task group:', error))
  }

  // Turns "Group similar" on or off for this person, then reloads the list:
  // the data tree is a table option, so the table is built again.
  toggleGroupSimilar(event) {
    event?.preventDefault()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch('/tasks/group_similar', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ enabled: !this.groupSimilarValue })
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        Turbo.visit(window.location.href, { action: "replace" })
      })
      .catch(error => console.error('Error saving the grouping preference:', error))
  }

  // Every listed task row, a closed group's members included, and no group
  // row: what the CSV, AI Magic Select, the drawer's assign and the reference
  // list hold. Grouped, Tabulator's own `active` rows are only the top level.
  recordRows() {
    if (!this.tabulatorTable) return []
    const rows = this.tabulatorTable.getRows('active')
    if (!this.groupSimilarValue) return rows
    return rows.flatMap(row => this.isGroupRow(row.getData()) ? row.getTreeChildren() : [row])
  }

  // Leaves a group's parent row out of selected or loaded data.
  recordsOnly(data) {
    return (data || []).filter(row => !this.isGroupRow(row))
  }

  // Select-all takes the top-level rows, and a group's row stands for no
  // task. Selecting a group's row selects the members shown under it, and
  // none that are hidden in a closed group, so the action bar never acts on
  // rows the person cannot see. Returns the selected tasks.
  groupedSelection(selected) {
    const parents = (selected || []).filter(row => this.isGroupRow(row.getData()))
    if (parents.length) {
      this.suppressSelectionPreviewRefresh = true
      try {
        parents.forEach(parent => {
          parent.deselect()
          if (parent.isTreeExpanded()) this.tabulatorTable.selectRow(parent.getTreeChildren())
        })
      } finally {
        this.suppressSelectionPreviewRefresh = false
      }
    }
    return this.recordsOnly(this.tabulatorTable.getSelectedData())
  }

  // Collapsing a group hides its members, so none stays selected: the action
  // bar never acts on rows the person cannot see.
  deselectHidden(parent) {
    this.suppressSelectionPreviewRefresh = true
    try {
      parent.getTreeChildren().forEach(child => child.deselect())
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }
  }

  // The rows arrow keys, ranges and the advance step move over: the visible
  // task rows, with a group's members once it is open.
  navigableRows() {
    if (!this.tabulatorTable) return []
    const rows = this.tabulatorTable.getRows(this.groupSimilarValue ? "display" : "active")
    return this.groupSimilarValue ? rows.filter(row => !this.isGroupRow(row.getData())) : rows
  }

  // The title alone: the state and the board have their own columns.
  titleFormatter(cell) {
    const data = cell.getRow().getData()
    if (this.isGroupRow(data)) return this.groupTitleFormatter(cell)
    const wrapper = document.createElement("span")
    wrapper.className = "wf-subject"
    const link = document.createElement("a")
    link.href = `/tasks/${data.reference}`
    link.className = "wf-subject-label"
    link.textContent = data.title || data.reference
    wrapper.appendChild(link)
    return wrapper
  }

  // The workflow's state as the house washi tape (PaperworkProcess#list_status),
  // the same as the workflow list's State cell; a routine open workflow keeps
  // plain text. A click filters to the state.
  workflowStateFormatter(cell) {
    const process = cell.getRow().getData().paperwork_process
    if (!process || !process.state) return ""
    const url = new URL(window.location.href)
    url.searchParams.set("process_state", process.state)
    const status = process.list_status
    return stateBadge(status, { href: url.toString(), title: `${status?.label} — workflow state: ${process.state}` })
      || createAttributeFilterFormatter("process_state", ["paperwork_process", "state"], true)(cell)
  }

  kindFormatter(cell) {
    const data = cell.getRow().getData()
    if (!cell.getValue()) return ""
    const url = new URL(window.location.href)
    url.searchParams.set("task_kind", data.task_kind)
    const mark = document.createElement("span")
    mark.className = data.task_kind === "__unclassified__" ? "kind-mark kind-mark--none" : "kind-mark"
    mark.title = [data.task_kind_name, data.task_reason].filter(Boolean).join(" — ")
    if (data.task_kind_emoji) {
      const emoji = document.createElement("span")
      emoji.className = "kind-mark__emoji"
      emoji.setAttribute("aria-hidden", "true")
      emoji.textContent = data.task_kind_emoji
      mark.append(emoji)
    }
    const link = document.createElement("a")
    link.href = url.toString()
    link.className = "kind-mark__name no-underline"
    link.dataset.turboPrefetch = "false"
    link.textContent = cell.getValue()
    mark.append(link)
    if (data.task_reason) {
      const reason = document.createElement("span")
      reason.className = "kind-mark__reason"
      reason.textContent = data.task_reason
      mark.append(reason)
    }
    return mark
  }

  // A due date in words, in the viewer's local time: "Overdue 2d", "Today",
  // "Tomorrow", or the date. The tones match PaperworkTasks::DueDates#cue and
  // the .due-cue styles: the accent only for a date that has passed.
  formatDue(value) {
    if (!value) return ""
    const due = new Date(value)
    if (Number.isNaN(due.getTime())) return ""

    const now = new Date()
    const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const days = Math.round((dayStart(due) - dayStart(now)) / 86400000)
    let tone = "later"
    let label = due.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    if (due < now) {
      tone = "overdue"
      label = days === 0 ? "Overdue" : `Overdue ${-days}d`
    } else if (days === 0) {
      tone = "soon"
      label = "Today"
    } else if (days === 1) {
      tone = "soon"
      label = "Tomorrow"
    }
    const full = due.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    return `<span class="due-cue due-cue--inline" data-tone="${tone}" title="Due ${full}">${label}</span>`
  }

  // Utility function to clean URL parameters
  cleanUrlParams(url) {
    const cleanUrl = new URL(url)
    cleanUrl.searchParams.delete('all_open')
    cleanUrl.searchParams.delete('all_on_hold')
    return cleanUrl
  }

  calculateTableHeight() {
    const headerElement = document.querySelector('.section-header-slim, .section-header')
    const filtersElement = document.querySelector('.section-filters')
    const mobileHeaderElement = document.querySelector('header.mobile') // Select the mobile-only header
    const queryTitleBannerElement = this.queryTitleBannerTarget

    // Calculate heights, checking for visibility
    const headerHeight = headerElement ? headerElement.offsetHeight : 0
    const filtersHeight = (filtersElement && window.getComputedStyle(filtersElement).display !== 'none') ? filtersElement.offsetHeight - 6 : 0
    const mobileHeaderHeight = (mobileHeaderElement && window.getComputedStyle(mobileHeaderElement).display !== 'none') ? mobileHeaderElement.offsetHeight : 0
    const queryTitleBannerHeight = (queryTitleBannerElement && !queryTitleBannerElement.classList.contains('hidden')) ? queryTitleBannerElement.offsetHeight : 0;

    // Combine all visible header heights
    const combinedHeight = mobileHeaderHeight + headerHeight + filtersHeight + queryTitleBannerHeight;
    return `max(240px, calc(100vh - ${combinedHeight}px))`
  }

  connect() {
    // Back to this list puts its scroll and open groups where they were.
    this.scrollMemory = new TableScrollMemory({ state: () => ({
      left: this.captureTableScrollLeft(),
      expanded: [...(this.expandedGroups || [])],
    }), onRestore: ({ left }) => this.restoreTableHorizontalScroll(left) })
    this.lastHorizontalScrollLeft = 0
    const expanded = this.scrollMemory.restored?.expanded
    if (expanded) this.expandedGroups = new Set(expanded)
    this.actionsTarget.style.display = 'none'
    this.selectedRowsValue = []
    this.activePreviewReference = null
    this.queryTitleBannerTarget.classList.add('hidden') // Ensure banner is hidden initially

    // `select=all` (a "review these tasks" link) lands with every listed task
    // selected and the drawer on the first. It is applied once, so it leaves
    // the address before anything remembers or reloads it.
    const landingUrl = new URL(window.location.href)
    this.pendingSelectAll = landingUrl.searchParams.get("select") === "all"
    if (this.pendingSelectAll) {
      landingUrl.searchParams.delete("select")
      history.replaceState(history.state, "", landingUrl.toString())
    }

    // Remember the list URL (with its current search/filters) so the task
    // header's "back to list" button can return here with the same view.
    try {
      localStorage.setItem('paperworkTaskListUrl', window.location.href)
    } catch (error) {
      console.error('Error storing task list URL in local storage:', error)
    }

    // Bind keyboard event handler
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)

    this.boundHandleTaskActioned = this.handleTaskActioned.bind(this)
    window.addEventListener('task-actioned', this.boundHandleTaskActioned)

    this.boundHandleBulkTaskActioned = this.handleBulkTaskActioned.bind(this)
    window.addEventListener('tasks-bulk-actioned', this.boundHandleBulkTaskActioned)

    this.boundOpenAssignListFromPreview = this.openAssignListFromPreview.bind(this)
    document.addEventListener('tasks-table:open-assign-list', this.boundOpenAssignListFromPreview)

    // Triage run started from the multi-select preview drawer.
    this.boundTriageFromPreview = () => this.triageSelectedTasks()
    document.addEventListener('tasks-table:triage', this.boundTriageFromPreview)

    // A row of the selection drawer's list steps into its task.
    this.boundStepIntoFromPreview = (event) => this.stepIntoSelected(event.detail?.reference)
    document.addEventListener('tasks-table:step-into', this.boundStepIntoFromPreview)
    this.boundDeselectFromPreview = (event) => this.deselectSelected(event.detail?.reference)
    document.addEventListener('tasks-table:deselect', this.boundDeselectFromPreview)
    this.boundClearFromPreview = () => this.clearSelection()
    document.addEventListener('tasks-table:clear-selection', this.boundClearFromPreview)

    this.boundUpdateSelectionActionsLayout = this.updateSelectionActionsLayout.bind(this)
    document.addEventListener('resource-preview:shown', this.boundUpdateSelectionActionsLayout)
    document.addEventListener('resource-preview:closed', this.boundUpdateSelectionActionsLayout)
    window.addEventListener('resize', this.boundUpdateSelectionActionsLayout)

    // Clear the active-row highlight when the preview drawer closes.
    this.boundClearActivePreviewRow = () => this.setActivePreviewRow(null)
    document.addEventListener('resource-preview:closed', this.boundClearActivePreviewRow)

    // Listen for bulk assign list selections from the quick-selector
    this.boundBulkAssignList = this.bulkAssignList.bind(this)
    document.addEventListener('bulk-assign-list:select', this.boundBulkAssignList)

    // Re-layout when the table is revealed on mobile (the cards<->table toggle).
    // Tabulator was initialized while its container was display:none (mobile
    // shows cards by default), so its columns measured 0-width and pinned to the
    // 40px minimum — redraw alone won't recover that. On the first reveal,
    // auto-size each column to its content (setWidth(true)); afterwards just
    // redraw. This event only fires from the mobile toggle, so the desktop
    // table (already visible at init) is never touched.
    this.boundReveal = () => {
      if (!this.tabulatorTable) return
      if (!this.tableAutoSized) {
        this.tableAutoSized = true
        try { this.tabulatorTable.getColumns().forEach((c) => c.setWidth(true)) } catch (_) {}
      }
      this.tabulatorTable.redraw(true)
    }
    this.element.addEventListener('index-view:reveal', this.boundReveal)

    // Calculate the height of the header and filters
    const headerElement = document.querySelector('.section-header-slim, .section-header')
    const filtersElement = document.querySelector('.section-filters')
    const mobileHeaderElement = document.querySelector('header.mobile') // Select the mobile-only header

    // Calculate heights, checking for visibility
    const headerHeight = headerElement ? headerElement.offsetHeight : 0
    const filtersHeight = (filtersElement && window.getComputedStyle(filtersElement).display !== 'none') ? filtersElement.offsetHeight : 0
    const mobileHeaderHeight = (mobileHeaderElement && window.getComputedStyle(mobileHeaderElement).display !== 'none') ? mobileHeaderElement.offsetHeight : 0

    // Combine all visible header heights
    const combinedHeight = mobileHeaderHeight + headerHeight + filtersHeight

    // "Group similar": look-alike tasks nest under one parent row, closed
    // until opened. Same columns, so the saved layout still fits.
    // A group the person opened stays open when the data reloads (after an
    // action in the drawer), so the advance to the next task finds its row.
    this.expandedGroups ||= new Set()
    const groupOptions = this.groupSimilarValue ? {
      dataTree: true,
      dataTreeStartExpanded: (row) => this.expandedGroups.has(row.getData().group?.key),
      dataTreeElementColumn: "title",
      dataTreeChildIndent: 18,
      ajaxParams: { group: "similar" },
    } : {}

    this.tableTarget.classList.toggle("task-table--grouped", this.groupSimilarValue)
    this.tabulatorTable = new DirectoryTable(this.tableTarget, {
      ...groupOptions,
      layout: "fitDataStretch",
      ...columnPersistenceOptions(this.agentKeyValue ? `tasks-table-v3-${this.agentKeyValue}` : "tasks-table-v3"),
      columnDefaults: {
        headerMenu: columnVisibilityHeaderMenu,
      },
      placeholder: emptyStateFor("tasks", { clearsSavedFilters: true }),
      height: this.calculateTableHeight(), // Use the helper method
      renderHorizontal: "basic",
      renderVertical: "virtual",
      // Re-apply the active-row highlight whenever a row (re)renders so it
      // survives data refreshes (the previewed row, not checkbox selection).
      rowFormatter: (row) => this.applyActiveStateToRow(row),
      selectableRows: true,
      // Plain row clicks must not change the selection — they only open the
      // preview. Returning false here stops Tabulator from attaching its
      // click-to-select handlers; the row-header checkboxes still select rows
      // programmatically (which bypasses this check).
      selectableRowsCheck: () => false,
      rowHeader: rowSelectionRowHeader({
        onRangeSelect: (row) => this.selectRangeTo(row),
        rowSelectable: (row) => !this.isGroupRow(row.getData()),
      }),
      dataSendParams: {
        "page": "page",
        "size": "length"
      },
      ajaxURL: this.urlValue,
      ajaxResponse: (url, params, response) => {
        const scrollLeft = this.captureTableScrollLeft()

        // Handle query title display
        const bannerText = [response.query_title, response.search_notice].filter(Boolean).join(' - ')
        if (bannerText) {
          this.queryTitleContentTarget.textContent = bannerText
          this.queryTitleBannerTarget.classList.remove('hidden')
        } else {
          this.queryTitleBannerTarget.classList.add('hidden')
        }
        // Recalculate and set height after potentially showing/hiding the banner
        this.setTableHeightPreservingHorizontalScroll(scrollLeft)
        return response.tasks
      },
      columns: 
      [
        { title: "Reference", field: "reference", frozen: false,
          accessorDownload: (value, data) => this.isGroupRow(data) ? `${data.group.count} look-alike tasks` : value,
          formatter: (cell) => {
            const reference = cell.getValue()
            if (!reference) return ""
            const link = document.createElement("a")
            link.href = `/tasks/${reference}`
            link.textContent = reference
            return link
          },
        },
        { title: "Title", field: "title", minWidth: 320, widthGrow: 3, formatter: (cell) => this.titleFormatter(cell) },
        // How long the task has sat, read at a glance; the exact Created and
        // Updated dates are in its hover and stay in the column menu.
        ageColumn({
          closed: (row) => ["completed", "cancelled", "rejected"].includes(row.state),
          closedAt: (row) => row.completed_at || row.updated_at,
        }),
        // The workflow's state (not the task's own) and its board come right after
        // the title: they say where the work stands, so they stay narrow and near
        // the front. The short header keeps the column narrow; its tooltip and the
        // CSV header spell out "Workflow state".
        { title: "State", headerTooltip: "Workflow state", titleDownload: "Workflow state", field: "paperwork_process.state", width: 108, minWidth: 96, formatter: (cell) => this.workflowStateFormatter(cell) },
        // Shown by default only when the account has boards to show.
        { title: "Board", field: "paperwork_process.paperwork_list_id", width: 150, minWidth: 120, visible: (this.listsValue || []).length > 0,
          formatter: (cell) => {
            const listId = cell.getValue()
            if (!listId) return '<span class="text-ink-soft text-sm">—</span>'
            
            const list = (this.listsValue || []).find(l => l.id === listId)
            if (!list) return '<span class="text-ink-soft text-sm">—</span>'
            
            const currentUrl = this.cleanUrlParams(window.location.href)
            currentUrl.searchParams.set("list_id", listId)

            return boardListMark(list, currentUrl.toString(), { compact: true })
          },
        },
        // { title: "Actions", field: "inline_actions", minWidth: 500, maxWidth: 700,
        //   formatter: (cell) => {
        //     const data = cell.getRow().getData();
        //     const reference = data.reference;
        //     return `<turbo-frame id="paperwork_task_${reference}_inline_action" src="/tasks/${reference}/inline_action?in_table_mode=1" loading="lazy" class="w-full block">Loading...</turbo-frame>`;
        //   },
        // },
        // { title: "State", field: "state", formatter: createAttributeFilterFormatter("state", "state", true) },
        // Without kinds every row would read "Unclassified", so the account keeps
        // the task-type column it already had until it defines one.
        { title: "Task kind", field: "task_kind_name", visible: this.taskKindsValue, formatter: (cell) => this.kindFormatter(cell) },
        { title: this.taskKindsValue ? "Task behavior" : "Task type", visible: !this.taskKindsValue, field: "type_display_name", formatter: createAttributeFilterFormatter("task_type_name", "task_type_name") },
        // The Workflow column links to the same workflow, so its reference
        // starts hidden.
        { title: "Workflow reference", field: "paperwork_process.reference", visible: false, formatter: "link", formatterParams: {
          url: (cell) => {
            const data = cell.getRow().getData()
            return `/processes/${data.paperwork_process?.reference}`
          },
        }},
        { title: "Workflow", field: "paperwork_process.title", formatter: "link", formatterParams: {
          url: (cell) => {
            const data = cell.getRow().getData()
            return `/processes/${data.paperwork_process?.reference}`
          },
        }},
        { title: "Related contact", field: "contact_at_issue.display_name", formatter: (cell) => {
            if (this.isGroupRow(cell.getRow().getData())) return this.groupPartyFormatter(cell)
            if (!cell.getValue()) {
              return "";
            }

            const data = cell.getRow().getData();

            const currentUrl = this.cleanUrlParams(window.location.href);
            currentUrl.searchParams.set("contact_at_issue_id", data.contact_at_issue.id);
            return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${cell.getValue()}</a>`;
          },
        },
        { title: "Agent", field: "agent.name", formatter: (cell) => {
            if (!cell.getValue()) {
              return "";
            }

            const data = cell.getRow().getData();

            const currentUrl = this.cleanUrlParams(window.location.href);
            currentUrl.searchParams.set("agent", data.agent.key);
            const link = document.createElement("a");
            link.href = currentUrl.toString();
            link.className = "inline-flex items-baseline gap-1 max-w-full min-w-0 no-underline";
            link.setAttribute("data-turbo-prefetch", "false");
            if (data.agent.icon) {
              const icon = document.createElement("span");
              icon.className = "shrink-0 no-underline";
              icon.setAttribute("aria-hidden", "true");
              icon.textContent = data.agent.icon;
              link.appendChild(icon);
            }

            const name = document.createElement("span");
            name.className = "min-w-0 truncate text-underline";
            name.textContent = cell.getValue();
            link.appendChild(name);
            return link;
          },
        },
        { title: "Assigned user", field: "assigned_user", // Change field to the object itself
          formatter: (cell) => {
            const data = cell.getRow().getData();
            if (!data.assigned_user) {
              return ""; // No assigned user
            }

            const user = data.assigned_user;
            let displayName = "";

            // Determine the display name based on available fields
            if (user.first_name && user.last_name) {
              displayName = `${user.first_name} ${user.last_name}`;
            } else if (user.email) {
              displayName = user.email;
            } else {
              // Fallback if somehow no name or email is present, though unlikely based on context
              displayName = "N/A"; 
            }

            // Create the filter link
            const currentUrl = this.cleanUrlParams(window.location.href);
            currentUrl.searchParams.set("assigned_user_id", user.id);
            return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${displayName}</a>`;
          },
        },
        // Contact and role assignment matter in a queue view; elsewhere they
        // start hidden to keep the table narrow.
        { title: "Assigned contact", field: "assigned_contact.display_name", visible: false, formatter: (cell) => {
            if (!cell.getValue()) {
              return "";
            }
            const data = cell.getRow().getData();
            if (!data.assigned_contact) return ""; // Defensive check

            const currentUrl = this.cleanUrlParams(window.location.href);
            currentUrl.searchParams.set("assigned_contact_reference", data.assigned_contact.reference);
            return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${cell.getValue()}</a>`;
          },
        },
        { title: "Assigned role", field: "assigned_role.name", visible: false, formatter: (cell) => {
            if (!cell.getValue()) {
              return "";
            }
            const data = cell.getRow().getData();
            if (!data.assigned_role) return ""; // Defensive check

            const currentUrl = this.cleanUrlParams(window.location.href);
            currentUrl.searchParams.set("assigned_role_id", data.assigned_role.id);
            return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${cell.getValue()}</a>`;
          },
        },
        // A due date is a soft aid: said in words, with the accent only once it has passed.
        { title: "Due", field: "due_at", formatter: (cell) => this.formatDue(cell.getValue()) },
        // The exact dates, hidden by default in favour of Age, in the viewer's zone.
        { title: "Created", field: "created_at", visible: false, formatter: "datetime", formatterParams: {
          inputFormat: "iso",
          outputFormat: "MMM d, yy 'at' t",
          invalidPlaceholder: "(invalid date)",
        }},
        { title: "Updated", field: "updated_at", visible: false, formatter: "datetime", formatterParams: {
          inputFormat: "iso",
          outputFormat: "MMM d, yy 'at' t",
          invalidPlaceholder: "(invalid date)",
        }},
      ].filter(column => !!column),
      dependencies: {
        DateTime: DateTime,
      },  
    })

    if (this.groupSimilarValue) {
      this.tabulatorTable.on("dataTreeRowExpanded", (row) => this.expandedGroups.add(row.getData().group?.key))
      this.tabulatorTable.on("dataTreeRowCollapsed", (row) => {
        this.expandedGroups.delete(row.getData().group?.key)
        this.deselectHidden(row)
      })
    }

    this.downloadButton = document.getElementById('download_csv')
    this.downloadButton?.addEventListener('click', this.boundDownloadCsv)

    bindRowPreviewPrefetch(this.tabulatorTable, {
      type: "tasks",
      isInteractive: target => this.isInteractiveElement(target),
      isGroup: data => this.isGroupRow(data),
      isSelectionPreview: row => (this.selectedRowsValue || []).length > 1 && row.isSelected(),
    })

    this.tabulatorTable.on("rowDblClick", (e, row) => {
      // Don't navigate when double-clicking the selection checkbox/gutter.
      if (e.target.closest('.tabulator-row-select')) return
      if (this.isGroupRow(row.getData())) return
      this.previewClickSequence = (this.previewClickSequence || 0) + 1
      Turbo.visit(`/tasks/${row.getData().reference}`)
    });

    this.tabulatorTable.on("rowClick", (e, row) => {
      if (this.isInteractiveElement(e.target)) return
      // Clicks in the selection gutter toggle the checkbox (handled by the
      // column's cellClick); don't also handle the row here.
      if (e.target.closest('.tabulator-row-select')) return
      // The first click already opened the drawer; the second belongs to the
      // double-click navigation and must not reload the same preview.
      if (e.detail > 1) return
      // A group's row opens and closes its members.
      if (this.isGroupRow(row.getData())) {
        row.treeToggle()
        return
      }

      // Clicking a row only browses it — the checkbox gutter is the only thing
      // that selects. Double-click opens the task.
      this.deferPreviewRow(row)
    })

    this.tabulatorTable.on("rowSelectionChanged", (data, rows, selected) => {
      // A group's row is never a task in the selection.
      data = this.groupSimilarValue ? this.groupedSelection(selected) : data
      this.selectedRowsValue = data
      this.updateSelectionActionsLayout()

      // Checking a row's checkbox opens/updates the preview to match the
      // selection, with the just-checked row as the current one. Programmatic
      // selection changes (arrow-nav, bulk ops) set suppressSelectionPreviewRefresh
      // and are ignored here; deselecting everything leaves the drawer as-is.
      if (this.suppressSelectionPreviewRefresh) return
      if (data.length === 0) return

      const justSelected = selected && selected.length ? selected[selected.length - 1] : null
      let currentReference = justSelected ? justSelected.getData().reference : this.currentPreviewReference
      if (!currentReference || !data.some(task => task.reference === currentReference)) {
        currentReference = data[data.length - 1]?.reference
      }

      if (data.length > 1) {
        this.openSelectionPreview(data, currentReference, justSelected)
      } else {
        const row = justSelected || this.recordRows().find(r => r.getData().reference === currentReference)
        if (row) this.openPreviewForRow(row)
      }
    })

    this.scrollMemory.attach(this.tabulatorTable)

    this.tabulatorTable.on("scrollHorizontal", (left) => {
      this.lastHorizontalScrollLeft = left || 0
    })

    this.tabulatorTable.on("dataSorting", () => {
      this.lastHorizontalScrollLeft = this.captureTableScrollLeft()
    })

    this.tabulatorTable.on("dataSorted", () => {
      this.restoreTableHorizontalScroll()
    })

    // Store task references in local storage when data is loaded
    this.tabulatorTable.on('dataLoaded', (data) => {
      // A failed load retains its rows. Clear retired elements only once new
      // data has arrived, before Tabulator replaces and formats those rows.
      this.clearActivePreviewElements()
      try {
        // Extract references from the loaded data (a group's members, not its row)
        const references = data.flatMap(task => this.isGroupRow(task) ? (task._children || []) : [task]).map(task => task.reference)
        
        // Store in local storage with a meaningful key
        localStorage.setItem('paperworkTaskReferences', JSON.stringify(references))
        
        console.log(`Stored ${references.length} task references in local storage`)
      } catch (error) {
        console.error('Error storing task references in local storage:', error)
      }

      this.restoreTableHorizontalScroll()
    })

    this.tabulatorTable.on("renderComplete", () => {
      this.restoreTableHorizontalScroll()
    })

    // The rows exist only once the data is processed. Phones show the card
    // list rather than the table, so the selection waits for a visible table.
    this.tabulatorTable.on("dataProcessed", () => {
      if (!this.pendingSelectAll) return
      this.pendingSelectAll = false
      if (this.tableTarget.getClientRects().length === 0) return
      const references = this.recordRows().map(row => row.getData().reference).filter(Boolean)
      this.selectReferences(references, references[0])
    })

    // Add event delegation to stop propagation on interactive elements
    this.clickTable = this.tableTarget
    this.clickTable.addEventListener('click', this.boundStopInteractiveClick, true)
  }

  downloadCsv() {
    // Grouped, the CSV is every listed task once, closed groups' members
    // included, and no group rows.
    const range = this.groupSimilarValue ? () => this.recordRows() : undefined
    this.tabulatorTable?.download('csv', `tasks-${DateTime.now().toFormat('yyyy-MM-dd-HH-mm')}.csv`, {}, range)
  }

  stopInteractiveClick(event) {
    // Selection checkboxes keep their ordinary click behavior.
    if (event.target.matches('input[type="checkbox"]')) return
    // A group's button handles its own click (and stops it at the button).
    if (event.target.closest?.('.task-group-title__act')) return
    // A link must reach Turbo, which makes it a visit (using any hover
    // prefetch) rather than a full page load. The row's click ignores links.
    if (event.target.closest?.('a[href]')) return
    if (this.isInteractiveElement(event.target)) event.stopPropagation()
  }

  captureTableScrollLeft() {
    if (!this.tabulatorTable) return this.lastHorizontalScrollLeft || 0

    const rowElement = this.tabulatorTable.rowManager?.getElement?.()
    const headerElement = this.tabulatorTable.columnManager?.getContentsElement?.()

    return Math.max(
      rowElement?.scrollLeft || 0,
      headerElement?.scrollLeft || 0,
      this.lastHorizontalScrollLeft || 0
    )
  }

  setTableHeightPreservingHorizontalScroll(scrollLeft = this.captureTableScrollLeft()) {
    if (!this.tabulatorTable) return

    const height = this.calculateTableHeight()
    if (this.tabulatorTable.options?.height !== height) {
      this.tabulatorTable.setHeight(height)
    }

    this.restoreTableHorizontalScroll(scrollLeft)
  }

  restoreTableHorizontalScroll(scrollLeft = this.lastHorizontalScrollLeft) {
    const table = this.tabulatorTable
    if (!table || table.destroyed || !Number.isFinite(scrollLeft) || scrollLeft < 0) return
    const request = (this.horizontalRestoreSequence || 0) + 1
    this.horizontalRestoreSequence = request
    if (this.horizontalScrollFrame != null) cancelAnimationFrame(this.horizontalScrollFrame)

    const restore = () => {
      if (this.tabulatorTable !== table || table.destroyed || this.horizontalRestoreSequence !== request) return
      try {
        table.rowManager?.scrollHorizontal?.(scrollLeft)
        table.columnManager?.scrollHorizontal?.(scrollLeft)
        this.lastHorizontalScrollLeft = scrollLeft
      } catch (_) {}
    }

    restore()
    this.horizontalScrollFrame = requestAnimationFrame(() => {
      if (this.horizontalRestoreSequence === request) this.horizontalScrollFrame = null
      restore()
    })
  }

  disconnect() {
    if (this.horizontalScrollFrame != null) cancelAnimationFrame(this.horizontalScrollFrame)
    this.horizontalScrollFrame = null
    this.horizontalRestoreSequence = (this.horizontalRestoreSequence || 0) + 1
    this.clearActivePreviewElements()
    this.downloadButton?.removeEventListener('click', this.boundDownloadCsv)
    this.downloadButton = null
    this.clickTable?.removeEventListener('click', this.boundStopInteractiveClick, true)
    this.clickTable = null
    this.scrollMemory?.detach()
    this.scrollMemory = null
    if (this.tabulatorTable) {
      this.tabulatorTable.destroy()
      this.tabulatorTable = null
    }
    if (this.boundReveal) {
      this.element.removeEventListener('index-view:reveal', this.boundReveal)
    }
    if (this.boundHandleKeydown) {
      document.removeEventListener('keydown', this.boundHandleKeydown)
    }
    if (this.boundHandleTaskActioned) {
      window.removeEventListener('task-actioned', this.boundHandleTaskActioned)
    }
    if (this.boundHandleBulkTaskActioned) {
      window.removeEventListener('tasks-bulk-actioned', this.boundHandleBulkTaskActioned)
    }
    if (this.boundOpenAssignListFromPreview) {
      document.removeEventListener('tasks-table:open-assign-list', this.boundOpenAssignListFromPreview)
    }
    if (this.boundTriageFromPreview) {
      document.removeEventListener('tasks-table:triage', this.boundTriageFromPreview)
    }
    if (this.boundStepIntoFromPreview) {
      document.removeEventListener('tasks-table:step-into', this.boundStepIntoFromPreview)
    }
    if (this.boundDeselectFromPreview) {
      document.removeEventListener('tasks-table:deselect', this.boundDeselectFromPreview)
      document.removeEventListener('tasks-table:clear-selection', this.boundClearFromPreview)
    }
    if (this.boundUpdateSelectionActionsLayout) {
      document.removeEventListener('resource-preview:shown', this.boundUpdateSelectionActionsLayout)
      document.removeEventListener('resource-preview:closed', this.boundUpdateSelectionActionsLayout)
      window.removeEventListener('resize', this.boundUpdateSelectionActionsLayout)
    }
    if (this.boundClearActivePreviewRow) {
      document.removeEventListener('resource-preview:closed', this.boundClearActivePreviewRow)
    }
    if (this.boundBulkAssignList) {
      document.removeEventListener('bulk-assign-list:select', this.boundBulkAssignList)
    }
  }

  handleKeydown(event) {
    if (this.hasOpenModal()) return
    if (this.handlePreviewNavigation(event)) return

    // Handle Escape key to clear selection
    if (event.key === 'Escape') {
      // Don't handle if we're in an input field (let the input handle escape)
      if (this.isInInputField(event.target)) return

      // Only clear selection if there are selected rows
      if (this.selectedRowsValue.length > 0) {
        event.preventDefault()
        this.clearSelection()
        return
      }
    }
  }

  handlePreviewNavigation(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return false
    if (!this.isResourcePreviewOpen()) return false
    if (this.isInInputField(event.target)) return false
    if (event.altKey || event.ctrlKey || event.metaKey) return false

    const direction = event.key === 'ArrowDown' ? 1 : -1
    event.preventDefault()

    if (event.shiftKey) {
      const row = this.previewNavigationRow(direction, { selectedOnly: false })
      if (!row) return true

      this.extendPreviewSelection(row)
      this.scrollRowIntoView(row)
      return true
    }

    // Plain arrow: same rule as a row click — move the preview without touching
    // the selection.
    const row = this.previewNavigationRow(direction, { selectedOnly: false })
    if (!row) return true

    this.previewRow(row)
    this.scrollRowIntoView(row)
    return true
  }

  previewNavigationRow(direction, options = {}) {
    if (!this.tabulatorTable) return null

    const activeRows = this.navigableRows()
    if (activeRows.length === 0) return null

    let navigableRows = activeRows
    if (options.selectedOnly) {
      const selectedReferences = new Set(this.selectedRowsValue.map(task => task.reference).filter(Boolean))
      navigableRows = activeRows.filter(row => selectedReferences.has(row.getData().reference))
    }

    if (navigableRows.length === 0) return null

    const currentReference = this.currentPreviewReference || this.selectedRowsValue[0]?.reference
    const currentIndex = navigableRows.findIndex(row => row.getData().reference === currentReference)
    const fallbackIndex = direction > 0 ? -1 : navigableRows.length
    const nextIndex = Math.max(0, Math.min(navigableRows.length - 1, (currentIndex >= 0 ? currentIndex : fallbackIndex) + direction))

    if (currentIndex === nextIndex) return null

    return navigableRows[nextIndex]
  }

  extendPreviewSelection(row) {
    const currentReference = this.currentPreviewReference || this.selectedRowsValue[0]?.reference
    const currentRow = this.navigableRows()
      .find(activeRow => activeRow.getData().reference === currentReference)

    this.suppressSelectionPreviewRefresh = true
    try {
      currentRow?.select()
      row.select()
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }

    this.selectedRowsValue = this.tabulatorTable.getSelectedData().filter(task => task.reference)
    this.currentPreviewReference = row.getData().reference

    if (this.selectedRowsValue.length > 1) {
      this.openSelectionPreview(this.selectedRowsValue, this.currentPreviewReference, row)
    } else {
      this.openPreviewForRow(row)
    }
  }

  scrollRowIntoView(row) {
    const table = this.tabulatorTable
    if (!table || table.destroyed) return Promise.resolve()

    // Offscreen virtual rows have no attached geometry. Let the renderer mount
    // and reveal the row; keep horizontal position and leave visible rows alone.
    const scrollLeft = this.captureTableScrollLeft()
    const request = (this.rowScrollSequence || 0) + 1
    this.rowScrollSequence = request
    return row.scrollTo("nearest", false)
      .then(() => {
        if (this.rowScrollSequence === request && this.tabulatorTable === table && !table.destroyed) {
          this.restoreTableHorizontalScroll(scrollLeft)
        }
      })
      .catch(error => {
        // A refresh/removal can retire the requested row while scrolling.
        if (this.rowScrollSequence === request && this.tabulatorTable === table && !table.destroyed) {
          console.error("Unable to reveal the task row:", error)
        }
      })
  }

  isInInputField(element) {
    const tagName = element?.tagName?.toLowerCase()
    if (!tagName) return false
    if (tagName === 'textarea' || tagName === 'select') return true
    if (element.isContentEditable) return true
    if (tagName === 'input') {
      // Only text-entry inputs should swallow keys like Escape/arrows. A focused
      // selection checkbox must not block Escape-to-deselect or arrow nav.
      const type = (element.getAttribute('type') || 'text').toLowerCase()
      return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file'].includes(type)
    }
    return false
  }

  isInteractiveElement(element) {
    if (!element) return false

    return !!element.closest(
      'a, button, input, select, textarea, summary, [role="button"], [data-no-resource-preview], turbo-frame, .btn, .link',
    )
  }

  isResourcePreviewOpen() {
    const panel = document.querySelector('[data-resource-preview-target="panel"]')
    return panel?.dataset.open === "true"
  }

  hasOpenModal() {
    return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], [role="dialog"]'))
      .some((element) => !element.classList.contains("hidden") && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden")
  }

  updateSelectionActionsLayout() {
    if (!this.hasActionsTarget) return

    const hasSelection = this.selectedRowsValue.length > 0
    const previewOpen = this.isResourcePreviewOpen()
    const desktopPreview = window.matchMedia('(min-width: 1280px)').matches
    const shouldShow = hasSelection && (!previewOpen || desktopPreview)

    this.actionsTarget.style.display = shouldShow ? 'flex' : 'none'
    this.actionsTarget.style.right = previewOpen && desktopPreview ? 'calc(32rem + 1rem)' : '1rem'
  }

  openPreviewForRow(row, options = {}) {
    const task = row.getData()
    if (!task.reference) return

    if (options.selectRow) {
      this.suppressSelectionPreviewRefresh = true
      try {
        this.tabulatorTable.deselectRow()
        row.select()
      } finally {
        this.suppressSelectionPreviewRefresh = false
      }
      this.selectedRowsValue = this.tabulatorTable.getSelectedData().filter(task => task.reference)
    }

    const selectedTasks = this.selectedRowsValue || []
    const selectedReferences = selectedTasks.map(selectedTask => selectedTask.reference).filter(Boolean)
    this.currentPreviewReference = task.reference
    this.setActivePreviewRow(task.reference, row)

    if (selectedReferences.length > 1 && selectedReferences.includes(task.reference)) {
      this.openSelectionPreview(selectedTasks, task.reference, row, { step: true })
      return
    }

    document.dispatchEvent(
      new CustomEvent("resource-preview:open", {
        detail: {
          type: "task",
          id: task.reference,
          title: task.title || task.reference,
          url: `/tasks/${task.reference}/preview`,
        },
      }),
    )
  }

  // The selection drawer lists the selection under its bulk actions; `step`
  // opens one task inside it instead (browsing a selected row, arrow keys).
  openSelectionPreview(tasks, currentReference, row = null, { step = false } = {}) {
    const references = tasks.map(task => task.reference).filter(Boolean)
    if (references.length === 0) return

    // Keep the navigation anchor in sync with the current row so arrow-key
    // navigation steps from the right place within a multi-selection; its
    // ring marks where the arrow keys go on from.
    if (currentReference) this.currentPreviewReference = currentReference
    this.setActivePreviewRow(currentReference, row)

    const params = new URLSearchParams()
    references.forEach(reference => params.append("references[]", reference))
    if (step && currentReference) params.set("current_reference", currentReference)

    document.dispatchEvent(
      new CustomEvent("resource-preview:open", {
        detail: {
          type: "task-selection",
          id: references.join(","),
          title: `${references.length} tasks selected`,
          url: `/tasks/selection_preview?${params.toString()}`,
        },
      }),
    )
  }

  stepIntoSelected(reference) {
    if (!reference || !this.selectedRowsValue.some(task => task.reference === reference)) return
    const row = this.recordRows().find(candidate => candidate.getData().reference === reference) || null
    this.openSelectionPreview(this.selectedRowsValue, reference, row, { step: true })
    if (row) this.scrollRowIntoView(row)
  }

  // Unchecking a row in the drawer: the table's own selection change redraws
  // the drawer for what is left (the list, one task's preview, or nothing).
  deselectSelected(reference) {
    const row = this.recordRows().find(candidate => candidate.getData().reference === reference)
    if (row?.isSelected()) row.deselect()
  }

  clearSelection() {
    // Clear selection in Tabulator table
    this.tabulatorTable.deselectRow()

    // Update the selectedRowsValue to empty array
    this.selectedRowsValue = []

    // Hide the actions panel since no rows are selected
    this.actionsTarget.style.display = 'none'
  }

  // Browsing a row via a plain click or arrow key: move the preview to it and
  // leave the checkbox selection alone. Rows are only ever selected from the
  // checkbox gutter (or shift+arrow), so browsing never builds a selection.
  previewRow(row) {
    this.openPreviewForRow(row)
  }

  deferPreviewRow(row) {
    const table = this.tabulatorTable
    const sequence = this.previewClickSequence = (this.previewClickSequence || 0) + 1
    setTimeout(() => {
      if (sequence === this.previewClickSequence && this.tabulatorTable === table && table && !table.destroyed) this.previewRow(row)
    })
  }

  // Shift+click in the checkbox gutter: select every row from the current row
  // (anchor) to this one, inclusive. Additive — never deselects.
  selectRangeTo(row) {
    const activeRows = this.navigableRows()
    const anchorReference = this.currentPreviewReference || this.selectedRowsValue[this.selectedRowsValue.length - 1]?.reference
    const anchorIndex = activeRows.findIndex(activeRow => activeRow.getData().reference === anchorReference)
    const targetIndex = activeRows.findIndex(activeRow => activeRow.getData().reference === row.getData().reference)

    if (anchorIndex === -1 || targetIndex === -1) {
      if (!row.isSelected()) row.select()
      return
    }

    const [from, to] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
    const rowsInRange = activeRows.slice(from, to + 1)

    this.suppressSelectionPreviewRefresh = true
    try {
      rowsInRange.forEach(rangeRow => rangeRow.select())
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }

    // Shift-clicking highlights text in the browser — clear it.
    window.getSelection()?.removeAllRanges()

    this.selectedRowsValue = this.tabulatorTable.getSelectedData().filter(task => task.reference)
    this.currentPreviewReference = row.getData().reference
    if (this.selectedRowsValue.length > 1) {
      this.openSelectionPreview(this.selectedRowsValue, this.currentPreviewReference, row)
    } else {
      this.openPreviewForRow(row)
    }
  }

  // Highlight the row whose preview drawer is open — the "current" row. Separate
  // from checkbox selection: shown as a ring (.tabulator-row-active) so it reads
  // distinctly from the selected (dark) fill, including on a selected row.
  setActivePreviewRow(reference, row = null) {
    this.clearActivePreviewElements()
    this.activePreviewReference = reference || null
    if (!this.tabulatorTable || !this.activePreviewReference) return
    // Data lookup does not create elements. Touch only the previous/new active
    // elements, including detached rows that can later re-enter the viewport.
    const activeRow = row || this.navigableRows().find(candidate => candidate.getData().reference === reference)
    if (activeRow) this.applyActiveStateToRow(activeRow)
  }

  applyActiveStateToRow(row) {
    const el = row.getElement()
    if (!el) return
    const active = !!this.activePreviewReference && row.getData().reference === this.activePreviewReference
    el.classList.toggle("tabulator-row-active", active)
    if (active) {
      // Ranked results can contain the same reference more than once. Track
      // only elements already initialized by the renderer or preview action.
      this.activePreviewElements ||= new Set()
      this.activePreviewElements.add(el)
    } else {
      this.activePreviewElements?.delete(el)
    }
  }

  clearActivePreviewElements() {
    this.activePreviewElements?.forEach(el => el.classList.remove("tabulator-row-active"))
    this.activePreviewElements?.clear()
  }

  handleTaskActioned() {
    if (!this.tabulatorTable) return

    // Working through a selection in the drawer: the task just acted on leaves
    // the selection and the drawer moves to the next selected task. The reload
    // rebuilds the rows, so the rest is selected again once they exist.
    const selectedReferences = this.selectedRowsValue.map(task => task.reference).filter(Boolean)
    if (this.isResourcePreviewOpen() && selectedReferences.length > 1 && selectedReferences.includes(this.currentPreviewReference)) {
      const index = selectedReferences.indexOf(this.currentPreviewReference)
      const remaining = selectedReferences.filter(reference => reference !== this.currentPreviewReference)
      const nextReference = remaining[Math.min(index, remaining.length - 1)]
      try {
        Promise.resolve(this.tabulatorTable.replaceData())
          .then(() => this.selectReferences(remaining, nextReference))
          .catch(() => {})
      } catch (error) {
        console.error('Unable to refresh task table after task action:', error)
      }
      return
    }

    // When a task is actioned from within the open preview drawer, advance to the
    // next task in the list so the user can keep working straight down the list
    // instead of the drawer closing after every action.
    const shouldAdvance =
      this.isResourcePreviewOpen() &&
      this.selectedRowsValue.length <= 1 &&
      !!this.currentPreviewReference

    const nextReference = shouldAdvance
      ? this.referenceAfter(this.currentPreviewReference)
      : null

    try {
      const result = this.tabulatorTable.replaceData()

      if (nextReference) {
        // Don't clear the preview — advance it to the next task once the refreshed
        // data has loaded so the row exists in the table.
        this.selectedRowsValue = []
        this.actionsTarget.style.display = 'none'
        Promise.resolve(result)
          .then(() => this.advanceToReference(nextReference))
          .catch(() => {})
      } else {
        this.clearSelection()
        document.dispatchEvent(new CustomEvent("resource-preview:close"))
      }
    } catch (error) {
      console.error('Unable to refresh task table after task action:', error)
    }
  }

  handleBulkTaskActioned() {
    if (!this.tabulatorTable) return

    this.clearSelection()
    this.currentPreviewReference = null
    this.setActivePreviewRow(null)
    document.dispatchEvent(new CustomEvent("resource-preview:close"))
  }

  // Reference of the task to advance to after `reference` is actioned: the next
  // active row, falling back to the previous one when actioning the last task.
  referenceAfter(reference) {
    if (!this.tabulatorTable) return null

    const activeRows = this.navigableRows()
    const index = activeRows.findIndex(row => row.getData().reference === reference)
    if (index === -1) return null

    const nextRow = activeRows[index + 1] || activeRows[index - 1]
    return nextRow ? nextRow.getData().reference : null
  }

  advanceToReference(reference) {
    if (!this.tabulatorTable) return

    const row = this.navigableRows()
      .find(activeRow => activeRow.getData().reference === reference)

    // The next task may have dropped out of the list on refresh — close cleanly.
    if (!row) {
      this.clearSelection()
      document.dispatchEvent(new CustomEvent("resource-preview:close"))
      return
    }

    this.openPreviewForRow(row, { selectRow: true })
    this.scrollRowIntoView(row)
  }

  // Selects these tasks (those still listed) and opens the drawer on
  // `currentReference`: the selection drawer for several, the task's own
  // preview for one, and closed for none. A closed group opens so no selected
  // task is hidden.
  selectReferences(references, currentReference) {
    if (!this.tabulatorTable) return

    const wanted = new Set(references)
    const rows = this.recordRows().filter(row => wanted.has(row.getData().reference))
    if (rows.length === 0) {
      this.clearSelection()
      document.dispatchEvent(new CustomEvent("resource-preview:close"))
      return
    }

    this.suppressSelectionPreviewRefresh = true
    try {
      if (this.groupSimilarValue) rows.forEach(row => { const parent = row.getTreeParent(); if (parent) parent.treeExpand() })
      this.tabulatorTable.deselectRow()
      this.tabulatorTable.selectRow(rows)
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }
    this.selectedRowsValue = this.recordsOnly(this.tabulatorTable.getSelectedData())
    this.updateSelectionActionsLayout()

    const currentRow = rows.find(row => row.getData().reference === currentReference) || rows[0]
    if (rows.length > 1) {
      this.openSelectionPreview(this.selectedRowsValue, currentRow.getData().reference, currentRow, { step: true })
    } else {
      this.openPreviewForRow(currentRow)
    }
    this.scrollRowIntoView(currentRow)
  }

  actionAll() {
    console.log('Action All:', this.selectedRowsValue)
    const baseUrl = this.actionAllButtonTarget.href.split('?')[0];
    const newUrl = baseUrl + '?' + this.selectedRowsValue.map(row => `references[]=${row.reference}`).join('&');
    this.actionAllButtonTarget.href = newUrl;
    this.actionAllButtonTarget.click()
  }

  aiMagicSelect() {
    // Get all visible/loaded data from the table
    const allVisibleTasks = this.recordRows().map(row => row.getData())
    
    if (allVisibleTasks.length === 0) {
      alert('No tasks are currently visible')
      return
    }
    
    console.log('AI Magic Select for tasks:', allVisibleTasks)
    
    // Create form data and submit as POST instead of GET with URL params
    const formData = new FormData()
    allVisibleTasks.forEach(row => {
      formData.append('ids[]', row.reference)
    })

    // Submit directly to ai_select_message endpoint
    const baseUrl = this.aiSelectButtonTarget.href.split('?')[0];
    fetch(baseUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim().startsWith("<turbo-stream")) {
        Turbo.renderStreamMessage(html)
        return
      }

      const modalFrame = document.querySelector('turbo-frame[id="modal"]')
      if (modalFrame) {
        modalFrame.innerHTML = html
      }
    })
    .catch(error => {
      console.error('Error loading AI select modal:', error)
      alert('Error loading AI Magic Select. Please try again.')
    })
  }

  openAssignList() {
    if (this.selectedRowsValue.length === 0) return

    // Listen for clicks on bulk assign list items
    const handler = (e) => {
      const item = e.target.closest('[data-bulk-assign-list-id]')
      if (!item) return
      e.preventDefault()
      document.removeEventListener('click', handler, true)
      const listId = item.dataset.bulkAssignListId
      document.dispatchEvent(new CustomEvent('bulk-assign-list:select', { detail: { listId } }))
    }
    document.addEventListener('click', handler, true)

    document.dispatchEvent(new CustomEvent("quick-selector:open", { detail: { id: "bulk_assign_list" } }))
  }

  openAssignListFromPreview(event) {
    const references = event.detail?.references || []
    if (references.length === 0) return

    const referenceSet = new Set(references)
    const rows = this.recordRows()
    const rowsToSelect = rows.filter(row => referenceSet.has(row.getData().reference))

    this.suppressSelectionPreviewRefresh = true
    try {
      this.tabulatorTable.deselectRow()
      this.tabulatorTable.selectRow(rowsToSelect)
    } finally {
      this.suppressSelectionPreviewRefresh = false
    }

    this.selectedRowsValue = this.recordsOnly(this.tabulatorTable.getSelectedData())
    this.openAssignList()
  }

  bulkAssignList(event) {
    const listId = event.detail?.listId
    const references = this.selectedRowsValue.map(row => row.reference)
    if (references.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch('/tasks/assign_list', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      },
      body: JSON.stringify({ references: references.join(','), list_id: listId || null })
    })
    .then(response => response.text())
    .then(html => {
      // Process turbo stream response (flash + table refresh)
      if (html.trim().startsWith("<turbo-stream")) {
        Turbo.renderStreamMessage(html)
      }
      // Also force table refresh
      try { this.tabulatorTable.replaceData() } catch(e) {}
      this.clearSelection()
    })
    .catch(error => {
      console.error('Error assigning list:', error)
    })
  }

  triageSelectedTasks() {
    if (this.selectedRowsValue.length === 0) {
      alert('Please select one or more tasks to triage')
      return
    }

    console.log('Reviewing selected tasks:', this.selectedRowsValue)

    // Extract task references from selected tasks
    const taskReferences = this.selectedRowsValue
      .map(task => task.reference)
      .filter(ref => ref)

    if (taskReferences.length === 0) {
      alert('No task references found')
      return
    }

    const params = new URLSearchParams()
    taskReferences.forEach(reference => params.append('references[]', reference))

    fetch(`/triage_runs/new?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim().startsWith("<turbo-stream")) {
        Turbo.renderStreamMessage(html)
        return
      }

      const modalFrame = document.querySelector('turbo-frame[id="modal"]')
      if (modalFrame) {
        modalFrame.innerHTML = html
      }
    })
    .catch(error => {
      console.error('Error loading review modal:', error)
      alert('Error loading review. Please try again.')
    })
  }

}
